import { WorkInProgress } from "@/services/api";
import format from "date-fns/esm/format";
import * as React from "react";
import { ServiceRequestFormModal } from "../ServiceRequestForm/ServiceRequestFormModal";
import { ViewRepairOrdersButton } from "../ViewRepairOrdersButton/ViewRepairOrdersButton";
import { AddRegular } from "@fluentui/react-icons";
import { FilterValue, IColumnDefinition, PCSTable } from "../PCSTable";
import { hasText } from "../../../../helpers/SearchHelpers";
import { Button, Field, Input, Spinner, Tooltip } from "@fluentui/react-components";
import { Search24Regular, FilterDismiss24Regular } from "@fluentui/react-icons";
import styles from "./WipTable.module.scss";
import { CurrencyCell } from "../../atoms/CurrencyCell/CurrencyCell";
import { useIsOnline } from "react-use-is-online";

export interface IWipTableProps {
	wipItems: WorkInProgress[];
	searchable?: boolean;
	loading: boolean;
	emptyMessage?: string;
}

export function WipTable({ wipItems, ...props }: IWipTableProps) {
	const { isOnline } = useIsOnline();
	const [selectedWIP, setSelectedWIP] = React.useState<WorkInProgress | null | undefined>();
	const [filter, setFilter] = React.useState<FilterValue[]>([]);

	const onSearch = React.useCallback((search: string, i: WorkInProgress) => {
		return hasText({ ...i, damageLines: [] }, search, true);
	}, []);

	const cols = React.useMemo<IColumnDefinition<WorkInProgress>[]>(() => {
		const defs: IColumnDefinition<WorkInProgress>[] = [
			{
				columnId: "actions",
				headerText: "",
				sizing: {
					idealWidth: 35,
					minWidth: 35,
				},
				onRender: (item) => {
					return (
						<Tooltip relationship="label" content={"New Service Request"}>
							<Button
								onClick={() => {
									setSelectedWIP(item);
								}}
								disabled={!isOnline}
								appearance="subtle"
								icon={<AddRegular />}></Button>
						</Tooltip>
					);
				},
			},

			{
				columnId: "roNumber",
				headerText: "RO #",
				getValue: (item) => item.roNumber,
				sortable: true,
			},
			{
				columnId: "vehicle",
				headerText: "Vehicle",
				getValue: (item) => item.vehicle,
				sortable: true,
				filterable: true,
				wrap: true,
				sizing: {
					minWidth: 200,
					idealWidth: 300,
					defaultWidth: 450,
				},
			},
			{
				columnId: "vin",
				headerText: "VIN",
				getValue: (item) => item.vin,
				sortable: true,
				sizing: {
					minWidth: 150,
					idealWidth: 150,
					defaultWidth: 150,
				},
			},
			{
				columnId: "insuranceCompany",
				headerText: "Insurance Company",
				getValue: (item) => item.insuranceCompany,
				sortable: true,
				filterable: true,
				wrap: false,
				sizing: {
					minWidth: 200,
				},
			},
			{
				columnId: "estimator",
				headerText: "Estimator",
				getValue: (item) => item.estimator,
				sortable: true,
				filterable: true,
			},

			{
				columnId: "roTotal",
				headerText: "RO Total $",
				getValue: (item) => item.total ?? 0,
				sortable: true,
				sizing: {
					defaultWidth: 50,
				},
				onRender(item) {
					return <CurrencyCell value={item.total ?? 0} />;
				},
			},
			{
				columnId: "prodStatus",
				headerText: "Production Status",
				filterable: true,
				getValue: (item) => item.productionStatus,
				sortable: true,
			},
			{
				columnId: "completedDate",
				headerText: "Completed Date",
				sortable: true,
				compare(a, b) {
					return (a.completedDate?.toISOString() ?? "").localeCompare(
						b.completedDate?.toISOString() ?? ""
					);
				},
				onRender: (item) => {
					return <div>{!!item.completedDate && format(item.completedDate, "P")}</div>;
				},
			},
			{
				columnId: "submitted",
				headerText: "Service Requests",
				onRender: (item) => {
					return <ViewRepairOrdersButton wip={item}></ViewRepairOrdersButton>;
				},
			},
		];
		return defs;
	}, [isOnline]);

	return (
		<div className={styles.wipTable}>
			<PCSTable
				items={wipItems}
				searchable
				onSearch={onSearch}
				enableShimmer={props.loading}
				columns={cols}
				defaultSortState={{
					sortColumn: "roNumber",
					sortDirection: "ascending",
				}}
				filters={filter}
				shimmerRows={10}
				onFiltersChanged={setFilter}></PCSTable>
			{!props.loading && wipItems.length === 0 && !!props.emptyMessage && (
				<div className={styles.empty}>
					<h3>{props.emptyMessage}</h3>
				</div>
			)}
			{props.loading && (
				<div className={styles.loading}>
					<Spinner labelPosition="below" size="large" label={"Loading..."}>
						Loading
					</Spinner>
				</div>
			)}
			<ServiceRequestFormModal
				wip={selectedWIP}
				onDismiss={() => {
					setSelectedWIP(null);
				}}></ServiceRequestFormModal>
		</div>
	);
}
